// ASSIGNMENT_INSTRUCTIONS_RENDERED_HEIGHT_V1

import {
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';


const DEFAULT_COLLAPSE_HEIGHT =
  180;


export default function AssignmentInstructions({
  html,
  collapseHeight =
    DEFAULT_COLLAPSE_HEIGHT
}) {
  const contentId =
    useId();

  const innerRef =
    useRef(null);

  /*
   * Start in the conservative collapsed state.
   *
   * useLayoutEffect measures ordinary text before the
   * browser paints, so short instructions immediately
   * become normal visible content without a flash.
   */
  const [
    isLong,
    setIsLong
  ] =
    useState(true);

  const [
    expanded,
    setExpanded
  ] =
    useState(false);


  const content =
    String(
      html || ''
    );


  useLayoutEffect(() => {
    const node =
      innerRef.current;

    if (!node) {
      return undefined;
    }


    setExpanded(
      false
    );


    function measure() {
      const renderedHeight =
        node
          .getBoundingClientRect()
          .height;


      const nextIsLong =
        renderedHeight >
        collapseHeight;


      setIsLong(
        previous =>
          previous ===
            nextIsLong
            ? previous
            : nextIsLong
      );
    }


    /*
     * Measure immediately for ordinary HTML.
     */
    measure();


    /*
     * ResizeObserver handles:
     *
     * - responsive width changes;
     * - images acquiring their natural height;
     * - other rich HTML changing its rendered size.
     */
    let observer =
      null;


    if (
      typeof ResizeObserver !==
      'undefined'
    ) {
      observer =
        new ResizeObserver(
          measure
        );

      observer.observe(
        node
      );
    }


    /*
     * The explicit image listener is a fallback for
     * WebViews where ResizeObserver behaviour around
     * late-loading images can be inconsistent.
     */
    const images =
      Array.from(
        node.querySelectorAll(
          'img'
        )
      );


    images.forEach(
      image => {
        image.addEventListener(
          'load',
          measure
        );
      }
    );


    window.addEventListener(
      'resize',
      measure
    );


    return () => {
      observer?.disconnect();


      images.forEach(
        image => {
          image.removeEventListener(
            'load',
            measure
          );
        }
      );


      window.removeEventListener(
        'resize',
        measure
      );
    };

  }, [
    content,
    collapseHeight
  ]);


  if (!content.trim()) {
    return null;
  }


  const collapsed =
    isLong &&
    !expanded;


  return (
    <section
      style={styles.box}
    >
      <style>
        {`
          .assignment-instructions-html img {
            max-width: 100%;
            height: auto;
          }

          .assignment-instructions-html video,
          .assignment-instructions-html iframe {
            max-width: 100%;
          }

          .assignment-instructions-html table {
            max-width: 100%;
          }
        `}
      </style>


      {isLong && (
        <button
          type="button"
          style={{
            ...styles.toggle,

            ...(expanded
              ? styles.toggleOpen
              : {})
          }}
          onClick={() =>
            setExpanded(
              previous =>
                !previous
            )
          }
          aria-expanded={
            expanded
          }
          aria-controls={
            contentId
          }
        >
          <span
            style={
              styles.toggleCopy
            }
          >
            <span
              style={
                styles.toggleTitle
              }
            >
              Assignment Instructions
            </span>

            <span
              style={
                styles.toggleHint
              }
            >
              {expanded
                ? 'Hide instructions'
                : 'View full instructions'}
            </span>
          </span>


          <span
            aria-hidden="true"
            style={{
              ...styles.chevron,

              transform:
                expanded
                  ? 'rotate(90deg)'
                  : 'rotate(0deg)'
            }}
          >
            ›
          </span>
        </button>
      )}


      <div
        id={contentId}
        style={{
          ...styles.clip,

          ...(collapsed
            ? styles.collapsed
            : {})
        }}
        aria-hidden={
          collapsed
            ? true
            : undefined
        }
      >
        <div
          ref={innerRef}
          className="assignment-instructions-html"
          style={{
            ...styles.content,

            ...(isLong
              ? styles.longContent
              : {}),

            visibility:
              collapsed
                ? 'hidden'
                : 'visible'
          }}
          dangerouslySetInnerHTML={{
            __html:
              content
          }}
        />
      </div>
    </section>
  );
}


const styles = {
  box: {
    marginBottom:
      '16px',

    border:
      '1px solid #d9e2ec',

    borderRadius:
      '8px',

    background:
      'white',

    overflow:
      'hidden'
  },


  toggle: {
    width:
      '100%',

    display:
      'flex',

    alignItems:
      'center',

    justifyContent:
      'space-between',

    gap:
      '14px',

    padding:
      '13px 15px',

    border:
      'none',

    background:
      'white',

    color:
      '#243b53',

    cursor:
      'pointer',

    textAlign:
      'left',

    fontFamily:
      'inherit'
  },


  toggleOpen: {
    borderBottom:
      '1px solid #e6edf2'
  },


  toggleCopy: {
    minWidth:
      0,

    display:
      'flex',

    flexDirection:
      'column',

    gap:
      '2px'
  },


  toggleTitle: {
    fontSize:
      '14px',

    lineHeight:
      1.3,

    fontWeight:
      '800',

    color:
      '#243b53'
  },


  toggleHint: {
    fontSize:
      '11px',

    lineHeight:
      1.35,

    fontWeight:
      '500',

    color:
      '#829ab1'
  },


  chevron: {
    flexShrink:
      0,

    color:
      '#2f6f9f',

    fontSize:
      '25px',

    lineHeight:
      1,

    transition:
      'transform 140ms ease'
  },


  clip: {
    maxHeight:
      'none',

    overflow:
      'visible'
  },


  collapsed: {
    maxHeight:
      0,

    overflow:
      'hidden'
  },


  content: {
    padding:
      '13px 15px',

    fontSize:
      '15px',

    lineHeight:
      1.7,

    color:
      '#243b53',

    overflowWrap:
      'anywhere'
  },


  longContent: {
    paddingTop:
      '14px',

    paddingBottom:
      '14px'
  }
};
